import * as Api from '@ViewModels';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const INFINITE_SURVEYS_QUERY_KEY = 'survey/eventRegistration';

export function useInfiniteEventRegistrationSurveys({
	shouldIncludeArchived = false,
	shouldIncludeExpired = false,
	search = '',
	pageSize = 100,
	enabled = true,
	impersonationContext,
	keepPreviousData: shouldKeepPreviousData = false,
	toastErrorMessage,
}: {
	shouldIncludeArchived?: boolean;
	shouldIncludeExpired?: boolean;
	search?: string;
	pageSize?: number;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	/** Retain the prior result while a new query key resolves (avoids blanking on filter changes). */
	keepPreviousData?: boolean;
	/** When set, a fetch failure raises a toast with this message (via the global QueryCache handler). */
	toastErrorMessage?: string;
}) {
	const userSession = useUserSession();
	return useInfiniteQuery<Api.IPagedCollection<Api.IEventRegistrationSurvey>>({
		enabled,
		placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
		getNextPageParam: lastPage => lastPage.pageToken,
		queryFn: async ({ pageParam }) => {
			const page = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IEventRegistrationSurvey>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: {
						includeArchived: shouldIncludeArchived,
						includeExpired: shouldIncludeExpired,
						search,
						pageToken: pageParam,
					},
					urlPath: `survey/eventRegistration`,
				}),
				'GET',
				null
			);

			return page;
		},
		queryKey: [
			INFINITE_SURVEYS_QUERY_KEY,
			shouldIncludeArchived,
			shouldIncludeExpired,
			search,
			pageSize,
			enabled,
			impersonationContext,
		],
		initialPageParam: '',
		meta: toastErrorMessage ? { toastErrorMessage } : undefined,
	});
}

export const invalidateInfiniteEventRegistrationSurveys = () => {
	reactQueryClient.invalidateQueries({ queryKey: [INFINITE_SURVEYS_QUERY_KEY] });
};
